import React, { useState, useRef, useEffect } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Box,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Chip,
  Tooltip,
  alpha,
  TextField,
  ClickAwayListener,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  ContentCopy as DuplicateIcon,
  DragIndicator as DragIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { PageListItem } from "../../types";

interface SortablePageItemProps {
  page: PageListItem;
  isSelected: boolean;
  primaryColor: string;
  icon: React.ReactNode;
  onSelect: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onRename?: (name: string, slug: string) => void;
}

/**
 * Convert a page name to a URL-safe slug.
 */
const toSlug = (name: string): string =>
  name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\u0E00-\u0E7F]+/g, "-")
    .replace(/^-+|-+$/g, "");

export const SortablePageItem: React.FC<SortablePageItemProps> = ({
  page,
  isSelected,
  primaryColor,
  icon,
  onSelect,
  onDelete,
  onDuplicate,
  onRename,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(page.name);
  const [editSlug, setEditSlug] = useState(page.slug);
  const nameInputRef = useRef<HTMLInputElement>(null);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: page.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : undefined,
  };

  // Focus name input when entering edit mode
  useEffect(() => {
    if (isEditing) {
      setTimeout(() => nameInputRef.current?.focus(), 50);
    }
  }, [isEditing]);

  const startEditing = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditName(page.name);
    setEditSlug(page.slug);
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditName(page.name);
    setEditSlug(page.slug);
  };

  const confirmEditing = () => {
    const trimmedName = editName.trim();
    const trimmedSlug = editSlug.trim().toLowerCase().replace(/[^a-z0-9-]/g, "-").replace(/^-+|-+$/g, "");

    if (!trimmedName || !trimmedSlug) {
      cancelEditing();
      return;
    }

    if (trimmedName !== page.name || trimmedSlug !== page.slug) {
      onRename?.(trimmedName, trimmedSlug);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    e.stopPropagation();
    if (e.key === "Enter") {
      confirmEditing();
    } else if (e.key === "Escape") {
      cancelEditing();
    }
  };

  // When editing, render inline form
  if (isEditing) {
    return (
      <ListItem ref={setNodeRef} style={style} disablePadding>
        <ClickAwayListener onClickAway={confirmEditing}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.75,
              width: "100%",
              p: 1,
              mx: 0.5,
              mb: 0.5,
              borderRadius: 2,
              bgcolor: alpha(primaryColor, 0.06),
              border: 1,
              borderColor: alpha(primaryColor, 0.3),
            }}
          >
            <TextField
              inputRef={nameInputRef}
              size="small"
              value={editName}
              onChange={(e) => {
                setEditName(e.target.value);
                // Auto-generate slug from name if slug hasn't been manually edited
                if (editSlug === toSlug(page.name) || editSlug === page.slug) {
                  setEditSlug(toSlug(e.target.value));
                }
              }}
              onKeyDown={handleKeyDown}
              placeholder="Page name"
              label="Name"
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": { borderRadius: 1.5 },
                "& .MuiInputBase-input": { fontSize: "0.85rem", py: 0.75 },
                "& .MuiInputLabel-root": { fontSize: "0.75rem" },
              }}
            />
            <TextField
              size="small"
              value={editSlug}
              onChange={(e) => setEditSlug(e.target.value.toLowerCase().replace(/\s+/g, "-"))}
              onKeyDown={handleKeyDown}
              placeholder="page-slug"
              label="Path"
              fullWidth
              InputProps={{
                startAdornment: (
                  <Box component="span" sx={{ color: "text.secondary", fontSize: "0.8rem", mr: 0.25 }}>
                    /
                  </Box>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": { borderRadius: 1.5 },
                "& .MuiInputBase-input": { fontSize: "0.85rem", py: 0.75 },
                "& .MuiInputLabel-root": { fontSize: "0.75rem" },
              }}
            />
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.5 }}>
              <Tooltip title="Cancel" arrow>
                <IconButton size="small" onClick={cancelEditing} sx={{ color: "grey.500", p: 0.5 }}>
                  <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Save" arrow>
                <IconButton
                  size="small"
                  onClick={confirmEditing}
                  sx={{ color: "success.main", p: 0.5 }}
                >
                  <CheckIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </ClickAwayListener>
      </ListItem>
    );
  }

  return (
    <ListItem
      ref={setNodeRef}
      style={style}
      disablePadding
    >
      <ListItemButton
        selected={isSelected}
        onClick={onSelect}
        sx={{
          borderRadius: 2,
          mb: 0.5,
          pr: 1,
          "&.Mui-selected": {
            bgcolor: alpha(primaryColor, 0.1),
            "&:hover": {
              bgcolor: alpha(primaryColor, 0.15),
            },
            "& .MuiListItemText-primary": {
              color: primaryColor,
              fontWeight: 600,
            },
            "& .MuiListItemText-secondary": { color: "text.secondary" },
            "& .MuiListItemIcon-root": {
              color: primaryColor,
            },
          },
          // Show action buttons on hover
          "&:hover .page-actions": {
            opacity: 1,
          },
        }}
      >
        {/* Drag Handle */}
        <Box
          {...attributes}
          {...listeners}
          sx={{
            display: "flex",
            alignItems: "center",
            cursor: "grab",
            color: "grey.400",
            mr: 0.5,
            "&:hover": { color: "grey.600" },
            "&:active": { cursor: "grabbing" },
          }}
        >
          <DragIcon fontSize="small" />
        </Box>
        <ListItemIcon sx={{ minWidth: 36, color: "grey.600" }}>{icon}</ListItemIcon>
        <ListItemText
          primary={page.name}
          secondary={`/${page.slug}`}
          primaryTypographyProps={{
            color: "text.primary",
            fontWeight: 500,
            fontSize: "0.9rem",
            noWrap: true,
          }}
          secondaryTypographyProps={{ color: "text.secondary", fontSize: "0.75rem", noWrap: true }}
          sx={{ mr: 1 }}
        />
        <Chip
          size="small"
          label={page.isPublished ? "Active" : "Inactive"}
          color={page.isPublished ? "success" : "default"}
          sx={{
            height: 20,
            fontSize: "0.65rem",
            flexShrink: 0,
            "& .MuiChip-label": { px: 1 },
          }}
        />
        {/* Action buttons */}
        <Box
          className="page-actions"
          sx={{
            display: "flex",
            gap: 0.25,
            ml: 0.5,
            opacity: 0,
            transition: "opacity 0.2s",
            flexShrink: 0,
          }}
        >
          {onRename && (
            <Tooltip title="Rename" arrow>
              <IconButton
                size="small"
                onClick={startEditing}
                sx={{
                  color: "grey.500",
                  p: 0.5,
                  "&:hover": { color: "info.main" },
                }}
              >
                <EditIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Duplicate" arrow>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onDuplicate();
              }}
              sx={{
                color: "grey.500",
                p: 0.5,
                "&:hover": { color: "primary.main" },
              }}
            >
              <DuplicateIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete" arrow>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              sx={{
                color: "grey.500",
                p: 0.5,
                "&:hover": { color: "error.main" },
              }}
            >
              <DeleteIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </ListItemButton>
    </ListItem>
  );
};
